import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import {
  BINARY_DECOMPOSITION,
  INVENTORY_UI,
  SNIPPET_DETECTION,
  VENDORED_DEPENDENCY_DETECTION,
} from '../../../../shared/featureFlags/flagNames';
import Locator from '../../../../shared/Locator';
import { useParams } from '../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { useSnippets } from '../../../hooks/api';
import useGetReleaseGroupComponentCount from '../../../hooks/api/binary/useGetReleaseGroupComponentCount';
import useGetRevisionComponentCount from '../../../hooks/api/binary/useGetRevisionComponentCount';
import ReleaseGroupModule from '../../../redux/project_grouping';
import Subnav from '../../generic/Subnav';
import SubnavCountBadge from '../../generic/Subnav/SubnavCountBadge';
import type { ProjectGroupRelease } from '../../../types';
import type { RevisionRouteParams } from '../projectDashboardTypes';

const isBinaryProject = (projectId: string): boolean => {
  return Locator.parse(projectId).fetcher === 'binary';
};

const shouldShowBinaryForReleaseGroup = (release: ProjectGroupRelease | null, isBinaryEnabled: boolean): boolean => {
  return isBinaryEnabled && (release?.projects?.some(project => isBinaryProject(project.projectId)) ?? false);
};

const shouldShowBinaryForProject = (projectId: string, isBinaryEnabled: boolean): boolean => {
  return isBinaryEnabled && isBinaryProject(projectId);
};

type ProjectScope = {
  type: 'project';
  projectId: string;
};

type ReleaseGroupScope = {
  type: 'releaseGroup';
  projectGroupId: number;
};

type Scope = ProjectScope | ReleaseGroupScope;

type BaseProps = {
  baseUrl: string;
  isLoading: boolean;
  dependencyCount: number;
  scope: Scope;
};

type InventorySubnavContentProps = {
  baseUrl: string;
  dependencyCount: number;
  componentCount: number | null;
  isFetchingBinaryCount: boolean;
  snippetsCount: number | null;
  isFetchingSnippetsCount?: boolean;
  shouldShowBinaryAnalysis: boolean;
  shouldShowSnippets: boolean;
  shouldShowVendored: boolean;
};

const InventorySubnavContent: VoidFunctionComponent<InventorySubnavContentProps> = ({
  baseUrl,
  dependencyCount,
  componentCount,
  isFetchingBinaryCount,
  snippetsCount,
  isFetchingSnippetsCount,
  shouldShowBinaryAnalysis,
  shouldShowSnippets,
  shouldShowVendored,
}) => {
  const totalCount = (
    (dependencyCount || 0) + (snippetsCount || 0) + (componentCount || 0)
  );
  return (
    <Subnav.Dropdown to={`${baseUrl}/browse/dependencies`} navKey="inventory">
      <Subnav.Dropdown.RootItem>
        Inventory
        <SubnavCountBadge
          count={totalCount}
          isLoading={dependencyCount === null}
        />
      </Subnav.Dropdown.RootItem>
      <Subnav.Dropdown.Item to={`${baseUrl}/browse/dependencies`} navKey="browse/dependencies">
        Dependencies
        <SubnavCountBadge count={dependencyCount || 0} isLoading={isFetchingBinaryCount} />
      </Subnav.Dropdown.Item>
      {/* Snippets are only available for project scope */}
      {shouldShowSnippets && (
        <Subnav.Dropdown.Item to={`${baseUrl}/browse/snippets`} navKey="snippets">
          Snippets
          <SubnavCountBadge count={snippetsCount || 0} isLoading={isFetchingSnippetsCount} />
        </Subnav.Dropdown.Item>
      )}
      {shouldShowBinaryAnalysis && (
        <Subnav.Dropdown.Item to={`${baseUrl}/browse/binary`} navKey="binary">
          Binary Analysis
          <SubnavCountBadge count={componentCount || 0} isLoading={componentCount === null} />
        </Subnav.Dropdown.Item>
      )}
      {shouldShowVendored && (
        <Subnav.Dropdown.Item to={`${baseUrl}/browse/vendored`} navKey="vendored">
          Vendored
          {/*
            TODO: Add the count for vendored dependencies here
            <SubnavCountBadge count={0} isLoading={false} />
          */}
        </Subnav.Dropdown.Item>
      )}
    </Subnav.Dropdown>
  );
};

type ProjectInventorySubnavItemProps = {
  baseUrl: string;
  dependencyCount: number;
  scope: ProjectScope;
  shouldShowBinaryAnalysis: boolean;
  shouldShowSnippets: boolean;
  shouldShowVendored: boolean;
};

const ProjectInventorySubnavItem: VoidFunctionComponent<ProjectInventorySubnavItemProps> = ({
  baseUrl,
  dependencyCount,
  scope,
  shouldShowBinaryAnalysis,
  shouldShowSnippets,
  shouldShowVendored,
}) => {
  const { revision_id: revisionId } = useParams<RevisionRouteParams>();
  const revisionLocator = `${scope.projectId}$${revisionId}`;
  const { data: componentCount, isLoading: isFetchingBinaryCount } = useGetRevisionComponentCount(revisionLocator);
  const { data: snippets, isLoading: isFetchingSnippets } = useSnippets({
    userRevisionLocator: revisionLocator,
    //  Do not add any other query params here, as we need this to be the unfiltered totalCount of snippets for this revision.
    params: {
      path: '/',
    },
  });

  return (
    <InventorySubnavContent
      baseUrl={baseUrl}
      dependencyCount={dependencyCount}
      componentCount={componentCount?.count ?? null}
      isFetchingBinaryCount={isFetchingBinaryCount}
      snippetsCount={snippets?.totalCount ?? null}
      isFetchingSnippetsCount={isFetchingSnippets}
      shouldShowBinaryAnalysis={shouldShowBinaryAnalysis}
      shouldShowSnippets={shouldShowSnippets}
      shouldShowVendored={shouldShowVendored}
    />
  );
};

type ReleaseGroupInventorySubnavItemProps = {
  baseUrl: string;
  dependencyCount: number;
  scope: ReleaseGroupScope;
  shouldShowBinaryAnalysis: boolean;
};

const ReleaseGroupInventorySubnavItem: VoidFunctionComponent<ReleaseGroupInventorySubnavItemProps> = ({
  baseUrl,
  dependencyCount,
  scope,
  shouldShowBinaryAnalysis,
}) => {
  const releaseId = useSelector(ReleaseGroupModule.selectors.getSelectedReleaseId);
  const {
    data: componentCount,
    isLoading: isFetchingBinaryCount,
  } = useGetReleaseGroupComponentCount(scope.projectGroupId, releaseId);

  return (
    <InventorySubnavContent
      baseUrl={baseUrl}
      dependencyCount={dependencyCount}
      componentCount={componentCount?.count ?? null}
      isFetchingBinaryCount={isFetchingBinaryCount}
      snippetsCount={null}
      shouldShowBinaryAnalysis={shouldShowBinaryAnalysis}
      shouldShowSnippets={false}
      shouldShowVendored={false}
    />
  );
};

/**
 * InventorySubnavItem contains the subnav item for the "Inventory" section.
 * For orgs where Snippet Scanning is not enabled, this is basically just the old Dependencies subnav item.
 * For orgs where Snippet Scanning is enabled, this is the Inventory dropdown.
 */
const InventorySubnavItem: VoidFunctionComponent<BaseProps> = ({ baseUrl, isLoading, dependencyCount, scope }) => {
  const isInventoryUIEnabled = useIsFeatureEnabled(INVENTORY_UI);
  const isSnippetScanningEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);

  const release = useSelector(ReleaseGroupModule.selectors.getRelease);

  const isProjectScope = scope.type === 'project';

  // Only consider True for snippets if we're looking at a project, since RG snippets are not supported yet.
  const shouldShowSnippets = isSnippetScanningEnabled && isProjectScope;
  //  Vendored dependencies are only available for project scope
  const shouldShowVendored = isVendoredDependenciesEnabled && isProjectScope;
  // We only show binary analysis for Projects that are for the binary fetcher or for Release Groups (since we don't have a binary project or not)
  const shouldShowBinaryAnalysis = scope.type === 'releaseGroup'
    ? shouldShowBinaryForReleaseGroup(release, isBinaryDecompositionEnabled)
    : shouldShowBinaryForProject(scope.projectId, isBinaryDecompositionEnabled);

  const shouldShowAnyGatedFeatures = shouldShowSnippets || shouldShowBinaryAnalysis || shouldShowVendored;
  //  They need to have at least one of the gated features enabled to show the inventory dropdown
  const shouldUseInventory = isInventoryUIEnabled && shouldShowAnyGatedFeatures;

  if (!shouldUseInventory) {
    return (
      <Subnav.ItemWithCount
        to={`${baseUrl}/browse/dependencies`}
        navKey="browse/dependencies"
        count={dependencyCount}
        isLoading={isLoading}
      >
        Dependencies
      </Subnav.ItemWithCount>
    );
  }

  if (scope.type === 'project') {
    return (
      <ProjectInventorySubnavItem
        baseUrl={baseUrl}
        dependencyCount={dependencyCount}
        scope={scope}
        shouldShowBinaryAnalysis={shouldShowBinaryAnalysis}
        shouldShowSnippets={shouldShowSnippets}
        shouldShowVendored={shouldShowVendored}
      />
    );
  }

  return (
    <ReleaseGroupInventorySubnavItem
      baseUrl={baseUrl}
      dependencyCount={dependencyCount}
      scope={scope}
      shouldShowBinaryAnalysis={shouldShowBinaryAnalysis}
    />
  );
};

export default InventorySubnavItem;
